import { useEffect, useState } from "react";
import { ArrowRight, LoaderCircle, LogOut } from "lucide-react";
import { useAuth } from "react-oidc-context";
import { Navigate, useLocation } from "react-router-dom";
import { Logo } from "@/components/layout/Logo";
import { getWorkspaceLandingPath, useWorkspaceAudience } from "@/contexts/WorkspaceAudienceContext";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";
import {
  forgetSession,
  rememberSession,
  returnToFromState,
  safeReturnTo,
  type ReturnState,
} from "@/auth/session";
import { createSignoutArgs } from "@/auth/oidcConfig";
import { useAutomaticSignin } from "@/auth/useAutomaticSignin";
import { Button, InlineMessage } from "@nexus/ui";

function SessionRestoration({ onTimeout }: { onTimeout: (value: boolean) => void }) {
  const [showFeedback, setShowFeedback] = useState(false);

  useEffect(() => {
    const feedbackTimer = window.setTimeout(() => setShowFeedback(true), 400);
    const timeoutTimer = window.setTimeout(() => onTimeout(true), 10_000);
    return () => {
      window.clearTimeout(feedbackTimer);
      window.clearTimeout(timeoutTimer);
    };
  }, [onTimeout]);

  return (
    <main
      className="flex min-h-screen items-center justify-center bg-background"
      role="status"
      aria-live="polite"
      aria-busy="true"
    >
      {showFeedback && (
        <div className="flex items-center gap-2 text-sm text-secondary">
          <LoaderCircle
            className="h-4 w-4 animate-spin text-brand-cyan motion-reduce:animate-none"
            aria-hidden
          />
          Reconnecting…
        </div>
      )}
    </main>
  );
}

export default function LoginPage() {
  const auth = useAuth();
  const { audience } = useWorkspaceAudience();
  const [redirectError, setRedirectError] = useState(false);
  const [manualSignin, setManualSignin] = useState(false);
  const [restorationTimedOut, setRestorationTimedOut] = useState(false);
  const [leaving, setLeaving] = useState(false);
  const isSigningIn = auth.activeNavigator === "signinRedirect";
  const location = useLocation();
  const returnTo =
    returnToFromState(location.state) ??
    safeReturnTo(new URLSearchParams(location.search).get("returnTo"));
  const automatic = useAutomaticSignin(returnTo);
  const recoveryFailed = automatic === "failed";
  const restoring = automatic === "waiting" || automatic === "signing-in";
  useDocumentTitle(restoring ? "Reconnecting" : "Sign in");
  // The provider state can still say "authenticated" for a token that has expired.
  const signedIn = auth.isAuthenticated && !auth.user?.expired;

  if (!auth.isLoading && signedIn)
    return <Navigate to={returnTo ?? getWorkspaceLandingPath(audience)} replace />;
  if (restoring && !restorationTimedOut && !redirectError)
    return <SessionRestoration onTimeout={setRestorationTimedOut} />;

  if (auth.isLoading && automatic === "off") {
    return (
      <main
        className="flex min-h-screen items-center justify-center bg-background"
        role="status"
        aria-live="polite"
      >
        <div className="flex items-center gap-2 text-sm text-secondary">
          <LoaderCircle
            className="h-4 w-4 animate-spin text-brand-cyan motion-reduce:animate-none"
            aria-hidden
          />
          Checking sign-in status…
        </div>
      </main>
    );
  }
  async function handleSignIn() {
    setRedirectError(false);
    setManualSignin(true);
    try {
      await auth.signinRedirect({ state: { returnTo } satisfies ReturnState });
    } catch {
      setRedirectError(true);
    }
  }

  async function restartSignin() {
    setLeaving(true);
    try {
      await auth.removeUser();
      forgetSession();
      rememberSession();
      window.location.replace(
        `/login${returnTo ? `?returnTo=${encodeURIComponent(returnTo)}` : ""}`,
      );
    } catch {
      setLeaving(false);
      setRedirectError(true);
    }
  }

  async function handleSignOut() {
    setLeaving(true);
    forgetSession();
    const fallback = () => window.location.replace("/login");
    window.setTimeout(fallback, 5_000);
    const args = {
      ...createSignoutArgs(auth.settings, window.location.origin),
      id_token_hint: auth.user?.id_token,
    };
    try {
      await auth.removeUser();
      await auth.signoutRedirect(args);
    } catch {
      fallback();
    }
  }

  return (
    <main className="relative min-h-screen overflow-hidden bg-background px-6 py-10 text-primary transition-colors duration-200">
      <div className="relative z-10 flex min-h-[calc(100vh-5rem)] items-center justify-center">
        <section className="w-full max-w-100">
          <div className="rounded-2xl border border-brand-navy/10 bg-white shadow-login transition-colors duration-200 dark:border-white/10 dark:bg-surface">
            <div className="h-1 rounded-t-2xl bg-brand-navy" />

            <div className="px-8 py-9">
              <div className="mb-8 flex items-center justify-center">
                <Logo variant="full" size={42} />
              </div>

              <div className="mb-8">
                <h1 className="text-lg font-semibold tracking-tight text-brand-navy dark:text-white">
                  Sign in to Nexus
                </h1>

                <p className="mt-2 text-sm leading-6 text-brand-grey-1 dark:text-white/55">
                  Use your organization account to access the internal platform.
                </p>
              </div>

              {(recoveryFailed ||
                redirectError ||
                (manualSignin && auth.error?.source === "signinRedirect" && !isSigningIn)) && (
                <InlineMessage
                  tone="error"
                  title={
                    recoveryFailed
                      ? "Sign-in could not be completed"
                      : "Sign-in could not be started"
                  }
                  className="mb-4"
                >
                  {recoveryFailed
                    ? "We couldn't restore your session. Sign in again or sign out to leave this session."
                    : "Check your connection and try again."}
                </InlineMessage>
              )}

              <Button
                variant="secondary"
                onClick={() => void (recoveryFailed ? restartSignin() : handleSignIn())}
                loading={!recoveryFailed && isSigningIn}
                disabled={
                  leaving || (!recoveryFailed && (auth.isLoading || Boolean(auth.activeNavigator)))
                }
                loadingLabel="Redirecting to SSO…"
                className="group h-11 w-full justify-between border-brand-navy/15 bg-white px-4 text-brand-navy hover:border-brand-cyan/50 hover:bg-background dark:border-white/10 dark:bg-white/3 dark:text-white dark:hover:border-brand-cyan/45 dark:hover:bg-white/6"
              >
                <span>{recoveryFailed ? "Sign in again" : "Continue with SSO"}</span>

                <ArrowRight
                  className="h-4 w-4 text-brand-grey-2 transition-transform duration-150 group-hover:translate-x-0.5 group-hover:text-brand-cyan"
                  strokeWidth={2}
                />
              </Button>

              {recoveryFailed && (
                <Button
                  variant="ghost"
                  className="mt-3 w-full"
                  disabled={leaving}
                  onClick={() => void handleSignOut()}
                >
                  <LogOut className="h-4 w-4" aria-hidden />
                  Sign out
                </Button>
              )}

              <div className="my-7 h-px bg-brand-navy/10 dark:bg-white/10" />

              <div className="flex items-center justify-center gap-4 text-xs text-muted">
                <span>© {new Date().getFullYear()} NAGRA Venturi</span>
              </div>
            </div>
          </div>
        </section>
      </div>
    </main>
  );
}
